// Вкладка «Ведомость»: какой шаблон применяется к объекту и какие в нём колонки.
//
// Тз, раздел 5.2, прямо называет её среди вкладок карточки объекта: «настройки ведомости
// (шаблон начислений и удержаний, унаследованный от типа объекта и при необходимости
// переопределённый)». Раздел 8.3 объясняет, зачем: «Добавление нового типа объекта или новой
// колонки удержания выполняется настройкой шаблона, а не доработкой кода». До 26.08.2026
// настройки не было ни одной: сервер умел и назначать шаблон объекту, и менять состав колонок,
// а в программе это не открывалось ниоткуда. То есть обещанная гибкость требовала разработчика
// — ровно того, чего тз обещает избежать.
//
// ## Два разных действия, и путать их нельзя
//
// «Какой шаблон» — про ЭТОТ объект. «Какие колонки» — про шаблон, а он общий: у объектового
// шаблона по умолчанию их шестьдесят четыре. Поэтому состав колонок правится только после
// того, как сказано, скольких объектов это коснётся.
//
// Какой шаблон действует, решает общая с «Обзором» функция (`site-template.ts`) — тремя
// уровнями, как сервер. До 23.09.2026 строка «Действует сейчас» у объекта на шаблоне по
// умолчанию стояла пустой, хотя выбор над ней писал «сейчас — шаблон по умолчанию».

import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { PayrollColumnOut, PayrollTemplateOut } from '@/shared/api/endpoints/payroll';
import type { SiteOut } from '@/shared/api/endpoints/org';
import { useSiteTypesQuery, usePayrollTemplatesQuery } from '@/shared/org/queries';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import * as Hint from '@/shared/ui/hint';
import { RowCheckbox } from '@/shared/ui/row-checkbox';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import type { ИзвестныйШаблон } from '../site-template';
import { шаблонОбъекта, шаблонПоУмолчанию } from '../site-template';
import { useSetSiteTemplate, useSetTemplateColumns } from '../template-queries';

const OBJECT_FORMS = ['объекту', 'объектам', 'объектам'] as const;

export function PayrollTemplateTab({ site }: { site: SiteOut }) {
  const шаблоны = usePayrollTemplatesQuery();
  const типы = useSiteTypesQuery();
  const назначение = useSetSiteTemplate();

  return (
    <div className='flex flex-col gap-4 p-5'>
      <ScreenState
        data={шаблоны.data}
        error={шаблоны.error}
        subject='справочник шаблонов ведомости'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void шаблоны.refetch()}
        loading={<Skeleton className='h-40 w-full' />}
        empty={
          <p className='text-paragraph-sm text-text-sub-600'>
            Шаблонов ведомости в системе нет ни одного. Так не бывает: объектовый и административный
            засеиваются при развёртывании — если их нет, база развёрнута не до конца, и об этом надо
            сказать администратору.
          </p>
        }
      >
        {(данные) => {
          const тип = типы.data?.items.find((строка) => строка.id === site.site_type_id);
          const отТипа = данные.items.find((строка) => строка.id === тип?.payroll_template_id);
          const поУмолчанию = шаблонПоУмолчанию(данные.items);
          const разрешение = шаблонОбъекта(site, данные.items);
          const действующий = разрешение.откуда === 'нет' ? undefined : разрешение.шаблон;

          return (
            <>
              <Block title='Какой шаблон применяется'>
                <div className='flex flex-col gap-3 p-4'>
                  <SelectField
                    id='site-template'
                    label='Шаблон этого объекта'
                    value={site.own_payroll_template_id ?? NO_VALUE}
                    noneLabel={
                      // Пока справочник типов едет, НЕ утверждаем, от чего наследуется: это
                      // тот же случай, что «0 ведомостей» поверх скелетов — готовый ответ там,
                      // где ответа ещё нет, и человек прочитает «шаблон по умолчанию» у
                      // объекта, у которого на самом деле есть тип со своим шаблоном.
                      //
                      // «Шаблон по умолчанию» пишется, только когда он и правда есть: без него
                      // наследовать нечего, и обещание в выборе было бы тем же противоречием,
                      // ради которого заведена общая функция разрешения.
                      типы.isPending
                        ? 'наследовать'
                        : отТипа !== undefined
                          ? `наследовать от типа «${тип?.title}» (${отТипа.title})`
                          : поУмолчанию !== undefined
                            ? 'наследовать (сейчас — шаблон по умолчанию)'
                            : 'наследовать (сейчас шаблона нет ни у типа, ни по умолчанию)'
                    }
                    onChange={(значение) =>
                      назначение.mutate({
                        siteId: site.id,
                        body: {
                          payroll_template_id: значение === NO_VALUE ? null : значение,
                        },
                      })
                    }
                    options={данные.items.map((строка) => ({
                      value: строка.id,
                      label: строка.title,
                    }))}
                    placeholder='выберите шаблон'
                    pending={шаблоны.isPending}
                    failed={шаблоны.isError}
                    failedError={шаблоны.error as ApiError | null}
                    hint='Свой шаблон перебивает унаследованный. Снимете его — объект вернётся к шаблону своего типа, а если типа нет, к шаблону по умолчанию.'
                  />
                  {назначение.isError && <ErrorAlert error={назначение.error as never} />}

                  {/* Значение без шаблона — ЯВНОЕ «нет» (null), а не пропуск: строка поля
                      показывает «не назначен» только на null, а на пропуске рисует пустоту. До
                      23.09.2026 так и было — у объекта на шаблоне по умолчанию «Действует
                      сейчас» стояло пустым. */}
                  <FieldList>
                    <FieldRow
                      label='Действует сейчас'
                      value={
                        разрешение.откуда === 'нет' ? null : (действующий?.title ?? 'назначен')
                      }
                      missing='шаблон не назначен'
                      missingNote='ни своего у объекта, ни от типа, ни по умолчанию: ведомость этого объекта собрать не из чего. Выберите шаблон выше.'
                      note={происхождение(разрешение, site.site_type_title)}
                    />
                    <FieldRow
                      label='Колонок в нём'
                      value={
                        действующий === undefined
                          ? null
                          : countLabel(действующий.columns_count, ['колонка', 'колонки', 'колонок'])
                      }
                      missing={
                        // Назначенный шаблон, которого нет в списке действующих, — не «шаблона
                        // нет»: строкой выше он назван назначенным, и две строки спорили бы.
                        разрешение.откуда === 'нет'
                          ? 'считать нечего: шаблона нет'
                          : 'не знаем: шаблона нет в списке действующих'
                      }
                      note={
                        действующий === undefined
                          ? undefined
                          : 'видимые и скрытые вместе; скрытая остаётся в шаблоне и в порядке'
                      }
                    />
                  </FieldList>
                </div>
              </Block>

              {/* Ключ по шаблону: несохранённая правка состава принадлежит ОДНОМУ шаблону.
                  Сменили шаблон объекта — черновик прежнего не должен уехать в новый. */}
              {действующий !== undefined && (
                <СоставКолонок
                  key={действующий.id}
                  шаблон={действующий}
                  поУмолчанию={действующий.id === поУмолчанию?.id}
                />
              )}
            </>
          );
        }}
      </ScreenState>
    </div>
  );
}

/** Откуда взялся шаблон — словами, потому что от этого зависит, поедет ли он у соседей. */
function происхождение(
  разрешение: ИзвестныйШаблон,
  типНазвание: string | null,
): string | undefined {
  switch (разрешение.откуда) {
    case 'свой':
      return 'задан этому объекту отдельно: у соседей по типу он не поменяется';
    case 'тип':
      return `унаследован от типа «${типНазвание ?? 'без названия'}»: поменяют его у типа — поедет и здесь`;
    case 'по умолчанию':
      return 'шаблон по умолчанию: ни своего у объекта, ни унаследованного от типа';
    case 'нет':
      // Следствие уже сказано в самой строке: повторять его пояснением незачем.
      return undefined;
  }
}

/**
 * Состав колонок шаблона: порядок, заголовок и видимость.
 *
 * Правится ЦЕЛИКОМ и отправляется одним списком: порядок задаётся порядком в списке, а не
 * числами снаружи — два источника одного порядка однажды разойдутся.
 */
function СоставКолонок({
  шаблон,
  поУмолчанию,
}: {
  шаблон: PayrollTemplateOut;
  /** Это шаблон по умолчанию: по нему считаются все объекты, которым не назначен другой. */
  поУмолчанию: boolean;
}) {
  const правка = useSetTemplateColumns();
  const [состав, setСостав] = useState<PayrollColumnOut[] | null>(null);
  const [готово, setГотово] = useState(false);

  const колонки = состав ?? шаблон.columns ?? [];
  const тронуто = состав !== null;
  // Заголовок правится по требованию, а не полем в каждой строке. В объектовом шаблоне
  // девятнадцать колонок, и девятнадцать полей ввода подряд — это полторы тысячи точек
  // прокрутки ради того, что меняют раз в год. Та же беда, что была с сеткой ролей в
  // настройках: девять галочек на человека при одной отмеченной.
  const [переименовываем, setПереименовываем] = useState<string | null>(null);
  // Счётчики сервера видят только ЯВНЫЕ назначения — объекту и типу. Объекты, дошедшие до
  // шаблона по умолчанию третьим уровнем, в них не попадают, и у шаблона по умолчанию счёт
  // бывает «ноль объектов» при десятках живых ведомостей. Поэтому он общий всегда.
  const общийШаблон = поУмолчанию || шаблон.sites_count + шаблон.site_types_count > 1;

  function подвинуть(индекс: number, куда: -1 | 1) {
    const новый = [...колонки];
    const сосед = индекс + куда;
    if (сосед < 0 || сосед >= новый.length) return;
    [новый[индекс], новый[сосед]] = [новый[сосед]!, новый[индекс]!];
    setСостав(новый);
    setГотово(false);
  }

  function поправить(индекс: number, что: Partial<PayrollColumnOut>) {
    const новый = [...колонки];
    новый[индекс] = { ...новый[индекс]!, ...что };
    setСостав(новый);
    setГотово(false);
  }

  return (
    <Block
      title='Какие в нём колонки'
      action={
        тронуто ? (
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xxsmall'
            onClick={() => {
              правка.mutate(
                {
                  templateId: шаблон.id,
                  body: {
                    version: шаблон.version,
                    columns: колонки.map((колонка) => ({
                      column_code: колонка.code,
                      title_override: колонка.title_override,
                      is_visible: колонка.is_visible,
                    })),
                  },
                },
                {
                  onSuccess: () => {
                    setСостав(null);
                    setГотово(true);
                  },
                },
              );
            }}
          >
            {правка.isPending ? 'Сохраняем…' : 'Сохранить состав'}
          </Button.Root>
        ) : undefined
      }
    >
      <div className='flex flex-col gap-3 p-4'>
        {общийШаблон && (
          // Сказано ДО правки, а не после: шаблон общий, и «поправил у себя» здесь неверно.
          <Alert.Root variant='light' status='warning' size='small' role='status'>
            <Alert.Icon as={RiErrorWarningLine} />
            <span>
              {поУмолчанию ? (
                <>
                  Это шаблон по умолчанию: по нему считается ведомость каждого объекта, которому не
                  назначен другой шаблон
                </>
              ) : (
                <>
                  Этот шаблон применяется к {countLabel(шаблон.sites_count, OBJECT_FORMS)}
                  {шаблон.site_types_count > 0 && (
                    <>
                      {' '}
                      и к{' '}
                      {countLabel(шаблон.site_types_count, [
                        'типу объектов',
                        'типам объектов',
                        'типам объектов',
                      ])}
                    </>
                  )}
                </>
              )}
              . Правка состава поменяет ведомость у всех сразу. Нужен свой набор колонок только
              здесь — сначала назначьте объекту отдельный шаблон.
            </span>
          </Alert.Root>
        )}

        {правка.isError && <ErrorAlert error={правка.error as never} />}
        {готово && (
          <Hint.Root>
            <Hint.Icon as={RiErrorWarningLine} />
            Состав сохранён. Ведомости, которые уже утверждены, не меняются: у них свой снимок
            условий расчёта.
          </Hint.Root>
        )}

        <ul className='flex flex-col divide-y divide-stroke-soft-200'>
          {колонки.map((колонка, индекс) => (
            <li key={колонка.code} className='flex flex-col gap-2 py-2'>
              <div className='flex items-center gap-3'>
                <RowCheckbox
                  id={`column-${колонка.code}`}
                  label={`Показывать колонку «${колонка.title}»`}
                  checked={колонка.is_visible}
                  onCheckedChange={(значение) => поправить(индекс, { is_visible: значение })}
                />
                <span className='flex min-w-0 flex-1 flex-col'>
                  <span className='truncate text-label-sm text-text-strong-950'>
                    {колонка.title}
                  </span>
                  <span className='truncate text-paragraph-xs text-text-sub-600'>
                    {колонка.kind_title}
                    {колонка.is_overridden && ' · заголовок заказчика'}
                    {!колонка.is_visible && ' · скрыта в ведомости'}
                  </span>
                </span>
                <span className='flex shrink-0 items-center gap-1'>
                  <Button.Root
                    type='button'
                    variant='neutral'
                    mode='stroke'
                    size='xxsmall'
                    onClick={() =>
                      setПереименовываем((прежний) =>
                        прежний === колонка.code ? null : колонка.code,
                      )
                    }
                  >
                    {переименовываем === колонка.code ? 'Свернуть' : 'Переименовать'}
                  </Button.Root>
                  <Button.Root
                    type='button'
                    variant='neutral'
                    mode='stroke'
                    size='xxsmall'
                    onClick={() => подвинуть(индекс, -1)}
                  >
                    Выше
                  </Button.Root>
                  <Button.Root
                    type='button'
                    variant='neutral'
                    mode='stroke'
                    size='xxsmall'
                    onClick={() => подвинуть(индекс, 1)}
                  >
                    Ниже
                  </Button.Root>
                </span>
              </div>
              {переименовываем === колонка.code && (
                <TextField
                  id={`column-title-${колонка.code}`}
                  label={`Заголовок в бланке · по каталогу «${колонка.catalog_title}»`}
                  value={колонка.title_override ?? ''}
                  onChange={(значение) =>
                    поправить(индекс, { title_override: значение === '' ? null : значение })
                  }
                  placeholder={колонка.catalog_title}
                  hint={
                    // Смысл колонки объясняет каталог, а не заголовок заказчика: у официальной
                    // пары слова стоят обратно смыслу, и без этой строки их «поправят».
                    колонка.source_note ?? колонка.behaviour_hint ?? undefined
                  }
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </Block>
  );
}
